// src/lib/hooks/useAsyncList.ts
import { useEffect, useRef, useState } from 'react';

type AsyncListState<T> = {
  data: T[];
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  loading: boolean;
  error: string | null;
};

/**
 * Generalizes the fetch/loading/error pattern repeated across Archive's list
 * views. `fetcher` is re-run whenever `deps` changes; `onLoaded` (optional)
 * receives the fetched array once, for count badges etc.
 *
 * `pollIntervalMs` (optional) re-runs `fetcher` on that cadence, matching
 * the Dashboard queue's existing 30s auto-refresh (useBookingQueue.ts).
 * Poll ticks are a background refresh — they don't flip `loading` back to
 * true, so the list doesn't flicker every 30s; only the initial fetch (or a
 * `deps` change, e.g. a new search filter) shows the loading state.
 */
export function useAsyncList<T>(
  fetcher: () => Promise<T[]>,
  deps: unknown[],
  fallbackErrorMessage: string,
  onLoaded?: (data: T[]) => void,
  pollIntervalMs?: number
): AsyncListState<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    let cancelled = false;
    isFirstLoad.current = true;

    function load() {
      if (isFirstLoad.current) {
        setLoading(true);
        setError(null);
      }
      fetcher()
        .then((result) => {
          if (cancelled) return;
          setData(result);
          onLoaded?.(result);
          setError(null);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : fallbackErrorMessage);
        })
        .finally(() => {
          if (cancelled) return;
          setLoading(false);
          isFirstLoad.current = false;
        });
    }

    load();

    let interval: ReturnType<typeof setInterval> | undefined;
    if (pollIntervalMs) {
      interval = setInterval(load, pollIntervalMs);
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, setData, loading, error };
}
